import "./App.css";
import { BrowserRouter, Routes, Route, useParams, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/toaster";
import ScrollToTop from "./components/ScrollToTop";
import ScrollTopButton from "./components/ScrollTopButton";
import HreflangTags from "./components/HreflangTags";
import { LanguageProvider } from "./i18n/LanguageContext";
import { CustomerAuthProvider } from "./context/CustomerAuth";
import AdGuard from "./components/AdGuard";
import { LANG_CODES, cityPath } from "./i18n/langPath";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import CoreServices from "./components/CoreServices";
import HowItWorks from "./components/HowItWorks";
import FleetSlider from "./components/FleetSlider";
import ChildSeats from "./components/ChildSeats";
import Destinations from "./components/Destinations";
import CustomerRatings from "./components/CustomerRatings";
import ReviewSchema from "./components/ReviewSchema";
import CitySearch from "./components/CitySearch";
import FAQ from "./components/FAQ";
import SeoContent from "./components/SeoContent";
import Newsletter from "./components/Newsletter";
import Footer from "./components/Footer";
import FloatingActions from "./components/FloatingActions";
import { lazy, Suspense } from "react";
const ChatWidget = lazy(() => import("./components/ChatWidget"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const BookingSuccess = lazy(() => import("./pages/BookingSuccess"));
const CitiesIndex = lazy(() => import("./pages/CitiesIndex"));
const LandingResolver = lazy(() => import("./components/LandingResolver"));
const Events = lazy(() => import("./components/Events")); // below-the-fold; keeps 266KB event data out of main bundle
const BlogList = lazy(() => import("./pages/BlogList"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const KontaktPage = lazy(() => import("./pages/KontaktPage"));
const KontoPage = lazy(() => import("./pages/KontoPage"));
const PricesPage = lazy(() => import("./pages/PricesPage"));
const EventsIndex = lazy(() => import("./pages/EventsIndex"));
const EventPage = lazy(() => import("./pages/EventPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const QuoteLinkResolver = lazy(() => import("./pages/QuoteLinkResolver"));
const StatusLookup = lazy(() => import("./pages/StatusLookup"));
const ArrivalPage = lazy(() => import("./pages/ArrivalPage"));
const DriverSharePage = lazy(() => import("./pages/DriverSharePage"));
const PickupGuidePage = lazy(() => import("./pages/PickupGuidePage"));

import { LANDING_ROUTES, LEGAL_ROUTES } from "./data/landingRoutes";

const Home = () => (
  <>
    <ReviewSchema />
    <Navbar />
    <main>
      <Hero />
      <CitySearch />
      <Destinations />
      <FleetSlider />
      <CoreServices />
      <HowItWorks />
      <ChildSeats />
      <Suspense fallback={<div style={{ minHeight: 420 }} />}>
        <Events />
      </Suspense>
      <FAQ />
      <SeoContent />
      <Newsletter />
      <CustomerRatings />
    </main>
    <Footer />
    <FloatingActions />
  </>
);

// Index under "/:lang". If the first URL segment is a real language code, show
// the localized home. Otherwise it is a German single-segment city keyword URL
// (e.g. /flughafentransfer-thun) that collides with the ":lang" param — resolve
// it to the correct CityPage so canonical German city pages never fall back to Home.
const LangIndex = () => {
  const { lang } = useParams();
  if (!lang || LANG_CODES.includes(lang)) return <Home />;
  return <LandingResolver seg={lang} lang="de" fallback={<Home />} />;
};

// Resolves a single URL segment: a city keyword landing page, a bare language
// home (/en), or the homepage fallback.
const SegResolver = () => {
  const { lang, seg } = useParams();
  if (!lang && LANG_CODES.includes(seg)) return <Home />; // e.g. /en (shouldn't reach, but safe)
  return <LandingResolver seg={seg} lang={lang || "de"} fallback={<Home />} />;
};

// Legacy /transfer/:slug -> localized city keyword URL.
const TransferRedirect = () => {
  const { lang, slug } = useParams();
  return <Navigate to={cityPath(slug, lang || "de")} replace />;
};

// Route tree rendered once at the root (German) and once under the /:lang prefix.
const AppRoutes = () => (
  <>
    <Route index element={<LangIndex />} />
    <Route path="booking" element={<BookingPage />} />
    <Route path="booking/success" element={<BookingSuccess />} />
    <Route path="booking/:seg1/:seg2" element={<BookingPage />} />
    <Route path="booking/:seg1/:seg2/:seg3" element={<BookingPage />} />
    <Route path="status" element={<StatusLookup />} />
    <Route path="status/:ref" element={<StatusLookup />} />
    <Route path="arrival/:ref" element={<ArrivalPage />} />
    <Route path="drive/:ref" element={<DriverSharePage />} />
    <Route path="staedte" element={<CitiesIndex />} />
    <Route path="transfer/:slug" element={<TransferRedirect />} />
    <Route path="blog" element={<BlogList />} />
    <Route path="blog/:slug" element={<BlogPost />} />
    <Route path="service/:slug" element={<ServicePage />} />
    <Route path="kontakt" element={<KontaktPage />} />
    <Route path="konto" element={<KontoPage />} />
    <Route path="preise" element={<PricesPage />} />
    <Route path="events" element={<EventsIndex />} />
    <Route path="events/:slug" element={<EventPage />} />
    <Route path="admin" element={<AdminPage />} />
    <Route path="q/:code" element={<QuoteLinkResolver />} />
    <Route path="airport-pickup-guide" element={<PickupGuidePage />} />
    <Route path="flughafen-abholung" element={<PickupGuidePage />} />
    {LANDING_ROUTES.map((s) => (
      <Route key={s} path={s} element={<LandingPage slug={s} />} />
    ))}
    {LEGAL_ROUTES.map((s) => (
      <Route key={s} path={s} element={<LegalPage slug={s} />} />
    ))}
    <Route path=":seg" element={<SegResolver />} />
  </>
);

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <LanguageProvider>
            <CustomerAuthProvider>
            <AdGuard>
            <ScrollToTop />
            <ScrollTopButton />
            <HreflangTags />
            <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
              <Routes>
                <Route path="/">{AppRoutes()}</Route>
                <Route path="/:lang">{AppRoutes()}</Route>
              </Routes>
            </Suspense>
            <Toaster />
            <Suspense fallback={null}><ChatWidget /></Suspense>
            </AdGuard>
            </CustomerAuthProvider>
          </LanguageProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
